import axios from "axios";

const API = axios.create({
    // baseURL: "http://127.0.0.1:8000"
    // baseURL: "https://test.api.mealno.in"
    baseURL: "https://api.mealno.in"

});

// event log
// export const logEvent = (event_type, { menu_item_id, vendor_id, meta } = {}) => {
//     API.post("/events/log", { event_type, menu_item_id, vendor_id, meta }).catch(() => { });
// };

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// [SECURITY] Shared refresh-and-retry logic. Used both when axios sees a real
// 401/403, and (defense-in-depth) when a response comes back as HTTP 200
// but its body says success:false with an auth-related error_code - in
// case any endpoint we haven't audited still masks auth failures as 200.
const handleAuthFailure = async (originalRequest, error) => {
    if (originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/login')) {
        return Promise.reject(error);
    }

    if (isRefreshing) {
        return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
        }).then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            originalRequest.baseURL = API.defaults.baseURL; // <-- Enforce URL
            return API(originalRequest);
        }).catch(err => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
        const refreshToken = localStorage.getItem("user_refresh_token");
        if (!refreshToken) throw new Error("No refresh token");

        const refreshClient = axios.create({ baseURL: API.defaults.baseURL });
        const res = await refreshClient.post('/auth/refresh', {
            refresh_token: refreshToken
        });

        const newAccessToken = res.data.access_token;
        localStorage.setItem("user_access_token", newAccessToken);

        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        originalRequest.baseURL = API.defaults.baseURL; // <-- Enforce URL

        processQueue(null, newAccessToken);
        isRefreshing = false;

        return API(originalRequest);

    } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);

        // Kill the dead session and kick to login
        localStorage.removeItem("user");
        localStorage.removeItem("user_access_token");
        localStorage.removeItem("user_refresh_token");
        window.location.href = "/login";

        return Promise.reject(refreshError);
    }
};

// 1. REQUEST INTERCEPTOR
API.interceptors.request.use((config) => {
    const token = localStorage.getItem("user_access_token");
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
});

// 2. RESPONSE INTERCEPTOR
API.interceptors.response.use(
    (response) => {
        // [SHIELD] THE BULLETPROOF SHIELD: Reject accidental HTML responses to stop cache poisoning
        if (typeof response.data === 'string' && response.data.trim().toLowerCase().startsWith('<!doctype html>')) {
            return Promise.reject(new Error("API returned an HTML page instead of JSON."));
        }

        // [SECURITY] DEFENSE-IN-DEPTH: a 200 response whose body is really an
        // auth failure (success:false + UNAUTHORIZED/FORBIDDEN) must NOT
        // be handed to pages as if it were real data. Route it through
        // the same refresh-and-retry path as a real 401/403.
        const body = response.data;
        const maskedAuthFailure =
            body && typeof body === 'object' && body.success === false &&
            (body.error_code === 'UNAUTHORIZED' || body.error_code === 'FORBIDDEN');

        if (maskedAuthFailure && !response.config._retry) {
            return handleAuthFailure(response.config, new Error(body.error || 'Unauthorized'));
        }

        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        if (!originalRequest) return Promise.reject(error);

        const status = error.response ? error.response.status : null;
        const isUnauthorized = status === 401 || status === 403;
        const isCorsHiddenError = !status && error.message === 'Network Error' && localStorage.getItem("user_access_token");

        if ((isUnauthorized || isCorsHiddenError) && !originalRequest._retry) {
            return handleAuthFailure(originalRequest, error);
        }
        return Promise.reject(error);

    }
);

export default API;
